import { Link } from 'react-router-dom';
import OfficialAvatar from '../OfficialAvatar';
import NotificationPrefs from '../NotificationPrefs';
import QuietStateCard from './QuietStateCard';

interface Adoption {
  id: string;
  behavingStatus?: string;
  changedSinceLastVisit?: boolean;
  notificationPreferences: any;
  official: {
    id: string;
    name: string;
    photoUrl: string;
    officeName: string;
    state: string;
    accountabilityScore: number | null;
  };
}

interface AdoptedOfficialListProps {
  adoptions: Adoption[];
  openPrefsId: string | null;
  setOpenPrefsId: (id: string | null) => void;
  behavingBadgeClass: (status?: string) => string;
  behavingLabel: (status?: string) => string;
}

export default function AdoptedOfficialList({
  adoptions,
  openPrefsId,
  setOpenPrefsId,
  behavingBadgeClass,
  behavingLabel,
}: AdoptedOfficialListProps) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-[10px] font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-1">
            Watchlist Roster
          </p>
          <h2 className="text-lg font-black uppercase tracking-widest text-navy-900 dark:text-cream-50">
            Your Watched Officials
          </h2>
        </div>
        <Link
          to="/my-adoptions"
          className="text-xs font-black uppercase tracking-widest text-gold-600 hover:underline dark:text-gold-400"
        >
          View All
        </Link>
      </div>

      {adoptions.length === 0 ? (
        <QuietStateCard
          eyebrow="Start Here"
          title="Build your watchlist"
          description="Adopt officials to get a movement-first home feed, alert controls, and weekly accountability context."
          ctaLabel="Browse Officials"
          ctaTo="/officials"
        />
      ) : (
        <div className="space-y-3">
          {adoptions.map((adoption) => (
            <div key={adoption.id} className="glass-panel rounded-lg p-4 transition hover:border-gold-400">
              <Link to={`/officials/${adoption.official.id}`} className="flex items-center gap-4">
                <OfficialAvatar
                  name={adoption.official.name}
                  photoUrl={adoption.official.photoUrl}
                  alt={adoption.official.name}
                  className="h-12 w-12 shrink-0 rounded-full bg-navy-100 dark:bg-navy-900 flex items-center justify-center"
                  imgClassName="h-12 w-12 rounded-full object-cover"
                  initialsClassName="text-sm font-black uppercase text-navy-500 dark:text-cream-300"
                />
                <div className="min-w-0 flex-grow">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="truncate font-bold text-navy-900 transition group-hover:text-gold-600 dark:text-cream-50 dark:group-hover:text-gold-400">
                      {adoption.official.name}
                    </p>
                    {adoption.changedSinceLastVisit && (
                      <span className="inline-flex rounded-full bg-gold-500/15 px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-gold-700 dark:text-gold-300">
                        Changed
                      </span>
                    )}
                  </div>
                  <p className="truncate text-xs text-navy-400 dark:text-navy-300">
                    {adoption.official.officeName}
                    {adoption.official.state ? ` · ${adoption.official.state}` : ''}
                  </p>
                </div>
                {adoption.behavingStatus && (
                  <span
                    className={`hidden shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-widest text-white md:inline-flex ${behavingBadgeClass(adoption.behavingStatus)}`}
                    title={`Status: ${behavingLabel(adoption.behavingStatus)}`}
                  >
                    {behavingLabel(adoption.behavingStatus)}
                  </span>
                )}
                <div className="shrink-0 text-right">
                  <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300">
                    Spine
                  </p>
                  <p className="text-lg font-black text-navy-900 dark:text-cream-50">
                    {adoption.official.accountabilityScore ?? '—'}
                  </p>
                </div>
              </Link>
              <NotificationPrefs
                officialId={adoption.official.id}
                initialPreferences={adoption.notificationPreferences}
                open={openPrefsId === adoption.id}
                onToggle={() => setOpenPrefsId(openPrefsId === adoption.id ? null : adoption.id)}
              />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
